import { useState, useEffect, useCallback } from 'react';
import { PackageData } from '@/pages/CreatePackage';
import { toast } from 'sonner';

const DRAFT_KEY = 'package_draft';
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

export const usePackageDraft = (initialData: PackageData) => {
  const [draftData, setDraftData] = useState<PackageData>(initialData);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Load draft from localStorage on mount
  useEffect(() => {
    const loadDraft = () => {
      try {
        const savedDraft = localStorage.getItem(DRAFT_KEY);
        if (savedDraft) {
          const parsed = JSON.parse(savedDraft);
          setDraftData(parsed.data);
          setLastSaved(new Date(parsed.timestamp));
          toast.info('تم استعادة المسودة المحفوظة');
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    };

    loadDraft();
  }, []);

  // Save draft to localStorage
  const saveDraft = useCallback((data: PackageData, showToast: boolean = false) => {
    setIsSaving(true);
    try {
      const draftToSave = {
        data,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draftToSave));
      setLastSaved(new Date());
      if (showToast) {
        toast.success('تم حفظ المسودة بنجاح');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('فشل حفظ المسودة');
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Auto-save effect
  useEffect(() => {
    const autoSaveTimer = setInterval(() => {
      if (draftData && Object.keys(draftData).length > 0) {
        saveDraft(draftData, false);
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(autoSaveTimer);
  }, [draftData, saveDraft]);

  // Clear draft
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_KEY);
      setDraftData(initialData);
      setLastSaved(null);
      toast.success('تم حذف المسودة');
    } catch (error) {
      console.error('Error clearing draft:', error);
      toast.error('فشل حذف المسودة');
    }
  }, [initialData]);

  // Update draft data
  const updateDraft = useCallback((updates: Partial<PackageData>) => {
    setDraftData(prev => ({ ...prev, ...updates }));
  }, []);

  // Manual save
  const manualSave = useCallback(() => {
    saveDraft(draftData, true);
  }, [draftData, saveDraft]);

  return {
    draftData,
    setDraftData,
    updateDraft,
    saveDraft: manualSave,
    clearDraft,
    lastSaved,
    isSaving,
  };
};
